import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, FilePlus2 } from "lucide-react";
import AnimatedActionIcon from "../AnimatedActionIcon";
import likeAnim from "../../assets/like.json";
import { fmtINR } from "../../lib/constants";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { toggleWishlist, isGuestWishlisted } from "../../lib/Wishlist";
import { pcbAdd, canBuildCatalog } from "../../lib/pcb";

export default function ProductCard({ product, price, onWishlist, isWishlisted }) {
  const { user } = useAuth();
  const img1 = product.images?.[0];
  const img2 = product.images?.[1];

  const [wished, setWished] = useState(false);
  const [likeTrigger, setLikeTrigger] = useState(0);

  const syncFromSource = () => {
    if (onWishlist) {
      // Parent controls the wishlist (e.g. Catalog) — trust the prop for everyone
      setWished(!!isWishlisted);
    } else if (user) {
      setWished(!!isWishlisted);
    } else {
      setWished(isGuestWishlisted(product.id));
    }
  };

  useEffect(() => {
    syncFromSource();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isWishlisted, product.id, onWishlist]);

  // Keep in sync if wishlist changes elsewhere (guest, only when card owns its state)
  useEffect(() => {
    const h = () => { if (!user && !onWishlist) setWished(isGuestWishlisted(product.id)); };
    window.addEventListener("wishlist-changed", h);
    return () => window.removeEventListener("wishlist-changed", h);
    /* eslint-disable-next-line */
  }, [user, product.id, onWishlist]);

  const handleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Parent-controlled: let it own the toggle, don't double-toggle.
    if (onWishlist) {
      if (!wished) setLikeTrigger((t) => t + 1);
      onWishlist(product.id);
      return;
    }
    try {
      const { action } = await toggleWishlist(product.id, !!user);
      setWished(action === "added");
      if (action === "added") setLikeTrigger((t) => t + 1);
      toast.success(`Wishlist ${action}`);
    } catch {
      toast.error("Could not update wishlist");
    }
  };

  return (
    <Link
      to={`/product/${product.id}`}
      data-testid={`product-card-${product.id}`}
      className="group hover-lift flex flex-col h-full relative bg-white border border-line rounded-2xl overflow-hidden min-w-0 w-full"
    >
      <div className="zoom-frame aspect-square sm:aspect-[4/3] bg-pearl relative overflow-hidden">
        <img
          src={img1}
          alt={product.name}
          loading="lazy"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ease-out ${img2 ? "group-hover:opacity-0" : ""}`}
        />
        {img2 && (
          <img
            src={img2}
            alt={`${product.name} alternate`}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover opacity-0 scale-90 transition-all duration-500 ease-out group-hover:opacity-100 group-hover:scale-100"
          />
        )}
        {product.is_bestseller && (
          <span className="absolute top-2 left-2 sm:top-3 sm:left-3 z-10 bg-maroon text-pearl text-[8px] sm:text-[9px] tracking-[0.15em] px-1.5 sm:px-2 py-1 font-semibold rounded-full shadow-sm">
            BESTSELLER
          </span>
        )}
        <button
          data-testid={`wishlist-toggle-${product.id}`}
          onClick={handleWishlist}
          aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
          className="absolute top-2 right-2 sm:top-3 sm:right-3 z-10 group/heart"
        >
          <AnimatedActionIcon
            animationData={likeAnim}
            trigger={likeTrigger}
            size={22}
            icon={<Heart size={19} className={`transition-colors drop-shadow ${wished ? "fill-red-600 stroke-red-600" : "fill-white/90 stroke-ink group-hover/heart:fill-red-600 group-hover/heart:stroke-red-600"}`} />}
          />
        </button>
        {user && canBuildCatalog(user.role) && (
          <button
            data-testid={`pcb-add-${product.id}`}
            onClick={(e) => {
              e.preventDefault(); e.stopPropagation();
              const added = pcbAdd(product);
              toast[added ? "success" : "info"](added ? "Added to Personal Catalog Builder" : "Already in your builder");
            }}
            title="Add to Personal Catalog Builder"
            className="absolute bottom-2 right-2 sm:bottom-3 sm:right-3 z-10 bg-maroon/90 hover:bg-maroon text-pearl w-7 h-7 sm:w-auto sm:h-auto sm:px-2.5 sm:py-1.5 rounded-full sm:rounded flex items-center justify-center gap-1 text-[10px] tracking-widest uppercase"
          >
            <FilePlus2 size={12}/> <span className="hidden sm:inline">Catalog</span>
          </button>
        )}
      </div>
      <div className="p-2.5 sm:p-4 min-w-0 flex flex-col flex-1 gap-1 sm:gap-1.5">
        <div className="eyebrow text-[8.5px] sm:text-[10px] tracking-[0.14em] sm:tracking-[0.22em] text-gold-dark truncate">
          {product.category}
        </div>
        <div className="font-display text-[13px] sm:text-lg text-ink leading-snug group-hover:text-maroon transition-colors line-clamp-2">
          {product.name}
        </div>
        <div className="flex items-center justify-between gap-2 mt-auto pt-1.5 sm:pt-2 flex-wrap">
          {typeof price === "number" && (
            <div className="text-[12px] sm:text-sm font-semibold text-maroon whitespace-nowrap">
              {/* Stocked in several weights: the card shows the cheapest, since
                  the customer picks the weight on the product page. */}
              {product.has_variants
                ? <>From {fmtINR(product.price_from ?? price)}</>
                : fmtINR(price)}
            </div>
          )}
          <div className="inline-flex items-center bg-maroon text-pearl rounded-full overflow-hidden text-[9px] sm:text-xs shrink-0 whitespace-nowrap ml-auto">
            {product.metal === "Gemstone" ? (
              <>
                {product.carat_weight ? <span className="px-1.5 sm:px-2.5 py-0.5 font-medium">{product.carat_weight} ct</span> : null}
                {product.colour ? <span className="px-1.5 sm:px-2.5 py-0.5 border-l border-pearl/25">{product.colour}</span> : null}
                {!product.carat_weight && !product.colour ? <span className="px-1.5 sm:px-2.5 py-0.5 font-medium">Gemstone</span> : null}
              </>
            ) : (
              <>
                {(product.purity && !["silver", "gemstone"].includes(String(product.metal || "").toLowerCase())) ? <span className="px-1.5 sm:px-2.5 py-0.5 font-medium">{product.purity}</span> : null}
                <span className={`px-1.5 sm:px-2.5 py-0.5 ${(product.purity && !["silver", "gemstone"].includes(String(product.metal || "").toLowerCase())) ? "border-l border-pearl/25" : ""}`}>{product.weight_grams}g</span>
              </>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}